// Bridge between the chat rich_content channel and the guide engine:
// converts a `ui_guide` payload (registry walkthrough id OR free-form AI steps)
// into an ActiveGuide. Invalid payloads fail loudly (toast + analytics),
// never silently.
import { toast } from "sonner";
import i18n from "@/lib/i18n";
import { track } from "@/lib/analytics";
import type { RichUiGuideContent } from "@/app/api/types/chat-sessions";
import { getGuideWalkthrough } from "./walkthroughs";
import { queryGuideTarget } from "./resolveTarget";
import { hasConsumedGuide, startGuide } from "./store";
import type { ActiveGuide, GuideSource, GuideStep } from "./types";

/** Does the current pathname already sit on `route` (exact or nested)? */
function isOnRoute(pathname: string, route: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function fromRegistry(
  payload: RichUiGuideContent,
  source: GuideSource,
): ActiveGuide | null {
  const walkthrough = getGuideWalkthrough(payload.walkthrough_id ?? "");
  if (!walkthrough) return null;
  const t = i18n.t.bind(i18n);

  const steps: GuideStep[] = walkthrough.steps.map((step) => ({
    target: step.target,
    title: step.titleKey ? t(step.titleKey) : undefined,
    body: t(step.bodyKey),
    route: step.route,
    routeExact: step.routeExact,
    advanceOn: step.advanceOn,
    advanceTo: step.advanceTo,
    appearTarget: step.appearTarget,
    safeZone: step.safeZone,
    advanceButtonPos: step.advanceButtonPos,
    captionSide: step.captionSide,
    optional: step.optional,
  }));

  // Context-aware entry: if the guide is launched from a different page than
  // the walkthrough's first step, prepend a step that highlights the sidebar link so
  // the user learns how to get there. It advances on arrival (navigate), so
  // clicking the highlighted link — or navigating any other way — moves on.
  const destination = walkthrough.steps[0]?.route;
  const currentPath =
    typeof window !== "undefined" ? window.location?.pathname : undefined;
  if (
    walkthrough.entry &&
    destination &&
    currentPath &&
    !isOnRoute(currentPath, destination)
  ) {
    steps.unshift({
      target: walkthrough.entry.target,
      body: t(walkthrough.entry.bodyKey),
      advanceOn: "navigate",
      advanceTo: destination,
    });
  }

  return {
    guideId: payload.guide_id,
    walkthroughId: walkthrough.id,
    title: t(walkthrough.titleKey),
    source,
    steps,
  };
}

function fromWireSteps(
  payload: RichUiGuideContent,
  source: GuideSource,
): ActiveGuide | null {
  const steps: GuideStep[] = [];
  for (const step of payload.steps ?? []) {
    if (!step.body) return null;
    // AI-authored guides carry NO route (and never navigate for the user): a
    // routeless step is always "on route", so the overlay never shows the
    // off-route "take me there" card — which cannot resolve a record page and
    // would nag the user. Navigation is instructed in text; the overlay waits
    // for each target to appear on the user's current screen.
    steps.push({
      target: step.target,
      title: step.title,
      body: step.body,
      advanceOn: step.advance_on,
    });
  }
  if (steps.length === 0) return null;
  return {
    guideId: payload.guide_id,
    title: payload.title,
    source,
    steps,
  };
}

export function buildGuideFromPayload(
  payload: RichUiGuideContent,
  source: GuideSource,
): ActiveGuide | null {
  if (payload.walkthrough_id) return fromRegistry(payload, source);
  return fromWireSteps(payload, source);
}

/**
 * Start a guide from a chat payload. `replay` bypasses the consumed-id dedupe
 * (user explicitly clicked "replay"); auto-start paths must keep it.
 */
export function startGuideFromPayload(
  payload: RichUiGuideContent,
  options: { replay?: boolean } = {},
): void {
  if (!payload.guide_id) return failVisibly(payload, "missing guide_id");
  if (!options.replay && hasConsumedGuide(payload.guide_id)) return;
  // Precondition gate: a walkthrough can require a marker that only exists in the
  // right context (e.g. batch-edit needs to be inside an editable order). If
  // it's missing, don't run out of context and don't scold with an error —
  // show a guide-style card telling the user how to unlock the walkthrough.
  const walkthrough = payload.walkthrough_id
    ? getGuideWalkthrough(payload.walkthrough_id)
    : undefined;
  if (walkthrough?.requires && !queryGuideTarget(walkthrough.requires)) {
    track("guide_precondition_unmet", {
      guide_id: payload.guide_id,
      walkthrough_id: walkthrough.id,
      requires: walkthrough.requires,
    });
    const t = i18n.t.bind(i18n);
    startGuide({
      guideId: `${payload.guide_id}:requires`,
      walkthroughId: walkthrough.id,
      title: t(walkthrough.titleKey),
      source: "registry",
      notice: true,
      steps: [
        {
          title: t(walkthrough.titleKey),
          body: t(walkthrough.requiresBodyKey ?? "guide.requiresEditableOrder"),
          advanceOn: "next",
        },
      ],
    });
    return;
  }
  let source: GuideSource;
  if (options.replay) source = "replay";
  else if (payload.walkthrough_id) source = "registry";
  else source = "ai";
  const guide = buildGuideFromPayload(payload, source);
  if (!guide) {
    return failVisibly(
      payload,
      payload.walkthrough_id
        ? `unknown walkthrough_id "${payload.walkthrough_id}"`
        : "invalid steps",
    );
  }
  startGuide(guide);
}

function failVisibly(payload: RichUiGuideContent, reason: string): void {
  toast.error(i18n.t("guide.unavailable"));
  track("guide_payload_rejected", {
    guide_id: payload.guide_id ?? null,
    walkthrough_id: payload.walkthrough_id ?? null,
    reason,
  });
}
